import styled, { css } from 'styled-components';
import { media, layout } from '@prisjakt/ui-base';
import {
  H1Text,
  H2Text,
  H3Text,
  TitleText,
  SubtitleText,
} from '@prisjakt/ui/text/variants';
import ExpertAvatarComponent from './components/expert-avatar';

export const AVATAR_SIZE = 32;

export const CardTitle = styled(SubtitleText)`
  a {
    &:hover {
      text-decoration: underline;
    }
  }
`;

export const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;

  ${media.mediumDown`
    grid-template-columns: minmax(0, 1fr);
  `};
`;

export const Main = styled.main``;

export const Article = styled.article`
  padding: ${layout.measurements.spacingNormal};
  padding-top: 0;
`;

type SidebarProps = {
  viewport?: 'mobile' | 'desktop';
};

export const Sidebar = styled.aside<SidebarProps>(
  p => css`
    display: flex;
    flex-direction: column;
    padding: 0 ${layout.measurements.spacingSmall};
    flex-shrink: 0;

    ${media.mediumDown`
      padding: 0 ${layout.measurements.spacingNormal};

      ${p.viewport === 'desktop' &&
        css`
          display: none;
        `}
    `};

    ${media.largeUp`
      padding-right: ${layout.measurements.spacingNormal};

      ${p.viewport === 'mobile' &&
        css`
          display: none;
        `}
    `};
  `
);

export const Header = styled.header`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-auto-flow: row;
  padding: ${layout.measurements.spacingNormal};
  row-gap: ${layout.measurements.spacingSmall};
  height: max-content;

  h1 {
    margin: 0;
  }

  ${media.mediumDown`
    padding-bottom: ${layout.measurements.spacingSmall};
  `}
`;

type ContentProps = {
  order?: number;
};

export const Content = styled.div<ContentProps>(
  p => css`
    margin-top: ${layout.measurements.spacingNormal};
    display: grid;
    grid-auto-flow: row;
    row-gap: ${layout.measurements.spacingSmall};
    order: 2;

    &:not(:first-of-type) {
      margin-top: ${layout.measurements.spacingNormal};
    }

    a:not([class]) {
      color: ${p.theme.text.callToAction};

      &:hover {
        color: ${p.theme.text.callToActionHover};
        text-decoration: underline;
      }
    }

    ${media.mediumDown`
      order: ${p.order || 2};
    `};
  `
);

export const Info = styled.span`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: start;
  column-gap: ${layout.measurements.spacingSmall};
`;

export const H1 = styled(H1Text)`
  line-height: 1.33;
`;

export const H2 = styled(H2Text)`
  line-height: 1.33;
`;

export const H3 = styled(H3Text)`
  font-weight: bold;
  line-height: 1.33;
`;

export const Subtitle = styled(SubtitleText)`
  && {
    color: ${p => p.theme.text.primary};
    font-size: ${layout.measurements.fontSizeNormal};
  }
`;

export const Tagline = styled(Subtitle)`
  && {
    color: ${p => p.theme.text.secondary};
  }
`;

export const Title = styled(TitleText)`
  &&,
  > * {
    font-size: ${layout.measurements.fontSizeMedium};
    font-weight: 700;
  }
`;

export const Media = styled.div`
  width: 100%;
`;

export const ContentType = styled.div`
  font-size: ${layout.measurements.fontSizeSmall};
  color: ${p => p.theme.text.secondary};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  width: 100%;
`;

export const UserInfo = styled.div`
  display: grid;
  grid-template-columns: ${AVATAR_SIZE}px auto;
  grid-template-rows: 1fr auto auto;
  column-gap: ${layout.measurements.spacingSmall};
`;

export const ExpertAvatar = styled(ExpertAvatarComponent)`
  grid-area: 1 / 1 / 4 / 2;
`;

export const UserName = styled(H3Text)`
  grid-area: 1 / 2 / 2 / 3;
  font-weight: 700;
  margin-bottom: 2px;
`;
